/**
 * Modello della superficie **Diagramma SLA** (piano Client sez. 5 F3b, sez. 6
 * riga 89 e sez. 8 riga 126).
 *
 * Cosa la superficie rende, e da dove viene (contratto letto nel working tree del
 * Server, sola lettura — fonte di verita' il codice, non il piano sez. 11.2):
 * - **nodi** da `GetDiagram` (`Flowenti-Server/TestServer/Services/Service.SLA.pas:805-927`,
 *   SQL alle righe 846-901): i nodi sono il driver della join (`activities` +
 *   `gateways` + `events` del modello), quindi anche i nodi **senza SLA** ci sono
 *   (`has_sla = false`);
 * - **stato del nodo** da `worst_health = max(health)` sulle istanze aperte
 *   (`Service.SLA.pas:869`): worst-state-wins, e un breach escalato resta un
 *   breach — il payload del diagramma non porta alcun livello di escalation, non
 *   esiste un sesto stato (sez. 5 F3b, sez. 14.1);
 * - **rapporto problematici/totale** da `problematic` (`count(*) filter (health > 0)`,
 *   `Service.SLA.pas:868`) su `active` (`count(*)`, `:863`). Con denominatore zero
 *   il rapporto e' **N/D dichiarato**, non `0%` (sez. 8 riga 135);
 * - **istanze su revisioni precedenti** da `other_revision_instances`
 *   (`count(*) filter (where source_model_revision <> ?)`, `Service.SLA.pas:872-874`).
 *   Attenzione al limite (sez. 11.3, decisione 9 del 2026-09-20): i nodi sono presi
 *   dal modello per `idprocessmodel`, quindi le istanze su revisioni precedenti il
 *   cui nodo **non esiste piu'** nel modello corrente non compaiono in nessuna
 *   riga del diagramma. Il Client lo **dichiara** (`SLA_DIAGRAM_REMOVED_NODES_LIMIT`)
 *   e non inventa un contenitore: il recupero resta nella lista Monitor (F3a);
 * - **revisione** da `meta.model_revision`, che il controller serializza come
 *   **stringa** (`Controller.SLA.pas:400`, `StrDict` + `ToString`): qui non si
 *   assume un numero. Il parametro `model_revision` e' opzionale: assente o non
 *   `> 0` fa usare la revisione corrente del modello (`Controller.SLA.pas:378-383`);
 * - **404** `SLA_MODEL_NOT_FOUND` per modello inesistente, cancellato, di altra
 *   organizzazione o revisione non risolvibile (`Controller.SLA.pas:381-388`).
 *
 * Tempi e durate sono valori **gia' calcolati dal Server** (`fl_sla_instance_clock`
 * `Service.SLA.pas:899`): qui nessun calendario, nessuna sottrazione nel browser
 * (sez. 1 e sez. 4). Il nodo piu' urgente e' scelto dal Server
 * (`node_urgent`, `:875-879`), non dal Client.
 *
 * Nessun campo e' aggiunto al DTO: le funzioni qui sotto leggono solo i campi del
 * contratto (`SlaDiagramNode`, `src/model/slaModel.ts:301-328`).
 */

import {
  SLA_HEALTH_AT_RISK,
  SLA_HEALTH_BREACHED,
  SLA_HEALTH_ON_TIME,
} from "model/slaModel";
import type { SlaDiagramNode, SlaHealth } from "model/slaModel";
import {
  SLA_MONITOR_STALE_AFTER_SECONDS,
  SLA_MONITOR_TEXT_KEYS,
  isSlaMonitorStale,
  slaMonitorHealth,
  slaMonitorHealthKey,
  slaMonitorNeedsRefetch,
} from "model/slaMonitorModel";

// ─── Chiavi di traduzione (superficie Diagramma, piano sez. 4) ───────────────

/**
 * Chiavi usate dalla superficie Diagramma. Raccolte qui e non sparse nel
 * componente perche' il piano sez. 4 le fa concordare con Python P1 in un
 * manifest: `getTranslation` restituisce la chiave se il testo manca, quindi
 * questo elenco **e'** il manifest lato Diagramma. La famiglia `sla.diagram.*`
 * non e' ancora nel catalogo P1 (residuo dichiarato): finche' manca, le label
 * escono grezze e le asserzioni "nessuna chiave `sla.*` residua" le **nominano**.
 *
 * Le label dei **tre stati di salute** non sono duplicate qui: sono le stesse del
 * Monitor (`SLA_MONITOR_TEXT_KEYS.health*`, vedi `SLA_DIAGRAM_REUSED_KEYS`), cosi'
 * un breach resta lo stesso testo nelle due superfici e il catalogo non si
 * sdoppia (sez. 4).
 */
export const SLA_DIAGRAM_TEXT_KEYS = {
  title: "sla.diagram.title",
  /** Selettore del modello: il diagramma e' model-scoped (sez. 11.2). */
  modelLabel: "sla.diagram.model.label",
  modelNone: "sla.diagram.model.none",
  /** Revisione opzionale: assente = revisione corrente risolta dal Server. */
  revisionLabel: "sla.diagram.revision.label",
  revisionAuto: "sla.diagram.revision.auto",
  revisionApplied: "sla.diagram.revision.applied",
  /** Canvas: nome accessibile e stato "non disegnato". */
  canvasLabel: "sla.diagram.canvas.label",
  canvasUnavailable: "sla.diagram.canvas.unavailable",
  zoomIn: "sla.diagram.zoom.in",
  zoomOut: "sla.diagram.zoom.out",
  zoomFit: "sla.diagram.zoom.fit",
  /** Elenco dei nodi: e' il dettaglio accessibile anche senza canvas (sez. 5 F3b). */
  nodesTitle: "sla.diagram.nodes.title",
  nodeOpen: "sla.diagram.node.open",
  nodeNoSla: "sla.diagram.node.no_sla",
  nodeNoInstances: "sla.diagram.node.no_instances",
  /** Badge di un nodo con SLA ma durata non leggibile (definizioni non disponibili). */
  nodeHasSla: "sla.diagram.node.has_sla",
  nodeInstances: "sla.diagram.node.instances",
  /** Rapporto problematici/totale, con N/D a denominatore zero (sez. 8 riga 135). */
  ratioLabel: "sla.diagram.ratio.label",
  ratioNotAvailable: "sla.diagram.ratio.not_available",
  /** Sintesi: quanti nodi, quanti senza SLA (il dettaglio li include). */
  summaryLabel: "sla.diagram.summary.label",
  summaryNodes: "sla.diagram.summary.nodes",
  summaryWithoutSla: "sla.diagram.summary.without_sla",
  summaryActive: "sla.diagram.summary.active",
  /** Dettaglio del nodo (popover/sheet). */
  detailTitle: "sla.diagram.detail.title",
  /** Drill-down alla lista: riusa la superficie F3a (sez. 5 F3b). */
  drillDown: "sla.diagram.drilldown",
  drillDownHint: "sla.diagram.drilldown.hint",
  /** Apre il designer del modello sul nodo, sezione SLA del pannello proprieta'. */
  configure: "sla.diagram.configure",
  /** `other_revision_instances` e i suoi limiti dichiarati (sez. 11.3). */
  otherRevisionsLabel: "sla.diagram.other_revisions.label",
  otherRevisionsLimit: "sla.diagram.other_revisions.limit",
  removedNodesLimit: "sla.diagram.removed_nodes.limit",
  /** Stato della superficie. */
  loading: "sla.diagram.loading",
  empty: "sla.diagram.empty",
  loadError: "sla.diagram.load_error",
  forbidden: "sla.diagram.forbidden",
  /** 404 dedicato della rotta: distinto da altri 404 (sez. 11.2). */
  modelNotFound: "sla.diagram.model_not_found",
} as const;

/**
 * Chiavi **gia' esistenti** riusate dalla superficie Diagramma: nessuna di
 * queste va duplicata in `sla.diagram.*` (piano sez. 4: non duplicare il
 * catalogo). Le tre label di salute sono le stesse del Monitor perche' gli stati
 * del contratto sono gli stessi (`SLA_HEALTH_*`); `sla.editor.fixture_notice` e'
 * l'avviso di modalita' fixture, identico per tutte le superfici SLA.
 */
export const SLA_DIAGRAM_REUSED_KEYS: string[] = [
  "generic.button.cancel",
  SLA_MONITOR_TEXT_KEYS.healthOnTime,
  SLA_MONITOR_TEXT_KEYS.healthAtRisk,
  SLA_MONITOR_TEXT_KEYS.healthBreached,
  SLA_MONITOR_TEXT_KEYS.asOf,
  SLA_MONITOR_TEXT_KEYS.timezone,
  SLA_MONITOR_TEXT_KEYS.stale,
  "sla.editor.fixture_notice",
];

// ─── Salute: i tre stati del contratto, nessun sesto ─────────────────────────

/**
 * I tre stati di salute **del diagramma**. E' la stessa lista del Monitor
 * (`SLA_MONITOR_HEALTH_STATES`), ri-esportata perche' la superficie Diagramma
 * non debba importare due moduli per la stessa regola. Il payload del diagramma
 * non porta l'escalation: `worst_health` e' `max(health)` e un breach escalato
 * resta un breach (sez. 5 F3b).
 */
export const SLA_DIAGRAM_HEALTH_STATES: readonly SlaHealth[] = [
  SLA_HEALTH_ON_TIME,
  SLA_HEALTH_AT_RISK,
  SLA_HEALTH_BREACHED,
];

/**
 * Salute di un nodo: `worst_health` se e' uno dei tre valori del contratto,
 * `null` altrimenti (nodo senza istanze aperte, `worst_health NULL`, oppure
 * valore fuori contratto). Non si ricava **mai** dai conteggi: e' il Server a
 * fare `max(health)` e il Client non ricalcola lo stato peggiore.
 */
export const slaDiagramWorstHealth = (
  node: Pick<SlaDiagramNode, "worst_health">,
): SlaHealth | null => slaMonitorHealth(node.worst_health);

/** Chiave di traduzione dello stato: la label non e' mai un enum. */
export const slaDiagramHealthKey = slaMonitorHealthKey;

/**
 * Forma dello stato, per l'icona: **non** e' il colore. Serve a garantire che lo
 * stato sia espresso anche in forma e testo e non solo in colore (sez. 5 riga 76)
 * e a dare un attributo asseribile (`data-health-icon`) senza leggere l'SVG.
 * `null` quando il nodo non ha stato (nessuna istanza aperta / senza SLA).
 */
export const slaDiagramHealthIcon = (health: SlaHealth | null): string | null => {
  switch (health) {
    case SLA_HEALTH_ON_TIME:
      return "check";
    case SLA_HEALTH_AT_RISK:
      return "alert";
    case SLA_HEALTH_BREACHED:
      return "error";
    default:
      return null;
  }
};

// ─── Rapporto problematici/totale ────────────────────────────────────────────

/**
 * Rapporto di un nodo. `total` e' `active` (istanze aperte del nodo,
 * `Service.SLA.pas:863`) e `problematic` e' il conteggio con `health > 0`
 * (`:868`): il rapporto si rende come coppia di numeri piu' percentuale, e con
 * `total === 0` la percentuale e' **N/D** (`percent: null`), non `0%` (sez. 8
 * riga 135). Il campo `available` distingue "nessun campione" da "campione con
 * zero problematici", che sono due stati diversi.
 */
export interface SlaDiagramNodeRatio {
  problematic: number;
  total: number;
  /** null quando total = 0: N/D, mai 0%. */
  percent: number | null;
  available: boolean;
}

export const slaDiagramNodeRatio = (
  node: Pick<SlaDiagramNode, "problematic" | "active">,
): SlaDiagramNodeRatio => {
  const total = Number.isFinite(node.active) && node.active > 0 ? node.active : 0;
  const problematic =
    Number.isFinite(node.problematic) && node.problematic > 0 ? node.problematic : 0;
  if (total === 0) {
    return { problematic: 0, total: 0, percent: null, available: false };
  }
  return {
    problematic: Math.min(problematic, total),
    total,
    percent: Math.round((Math.min(problematic, total) / total) * 100),
    available: true,
  };
};

/** "n/m" come coppia di numeri: `getTranslation` non interpola i parametri. */
export const slaDiagramNodeRatioValue = (ratio: SlaDiagramNodeRatio): string =>
  ratio.available ? `${ratio.problematic}/${ratio.total}` : "";

/** Percentuale formattata nella lingua dell'app, `null` quando non c'e' campione. */
export const slaDiagramNodeRatioPercent = (
  ratio: SlaDiagramNodeRatio,
  locale: string,
): string | null =>
  ratio.percent === null
    ? null
    : new Intl.NumberFormat(locale, {
        style: "percent",
        maximumFractionDigits: 0,
      }).format(ratio.percent / 100);

// ─── Sintesi del modello (inclusi gli SLA assenti) ───────────────────────────

/**
 * Sintesi sui nodi del modello. I nodi **senza SLA sono inclusi nel dettaglio**
 * (sez. 5 F3b): `withoutSla` li conta e il rapporto complessivo ha come
 * denominatore le istanze aperte dei nodi con SLA. Come il rapporto per nodo, con
 * denominatore zero la percentuale e' N/D e non `0%`.
 */
export interface SlaDiagramSummary {
  nodes: number;
  withSla: number;
  withoutSla: number;
  active: number;
  problematic: number;
  percent: number | null;
  /** Stato peggiore del modello: `max(worst_health)` sui nodi, `null` se nessuno. */
  worst: SlaHealth | null;
}

export const slaDiagramSummary = (
  nodes: SlaDiagramNode[],
): SlaDiagramSummary => {
  const withSla = nodes.filter((node) => node.has_sla).length;
  const active = nodes.reduce(
    (sum, node) => sum + (Number.isFinite(node.active) && node.active > 0 ? node.active : 0),
    0,
  );
  const problematic = nodes.reduce(
    (sum, node) =>
      sum + (Number.isFinite(node.problematic) && node.problematic > 0 ? node.problematic : 0),
    0,
  );
  const worst = nodes.reduce<SlaHealth | null>((current, node) => {
    const health = slaDiagramWorstHealth(node);
    if (health === null) return current;
    if (current === null) return health;
    return Math.max(current, health) as SlaHealth;
  }, null);
  const scoped = Math.min(problematic, active);
  return {
    nodes: nodes.length,
    withSla,
    withoutSla: nodes.length - withSla,
    active,
    problematic: scoped,
    percent: active === 0 ? null : Math.round((scoped / active) * 100),
    worst,
  };
};

/**
 * Tipi BPMN degli eventi di inizio e fine (tutte le varianti: timer, messaggio,
 * terminate... hanno lo stesso `type` in bpmn-js, cambia solo la
 * `eventDefinition`). Gli eventi oggi non portano SLA: sul canvas non ricevono
 * badge ne' marcatore, e non compaiono nell'elenco ne' nella sintesi.
 */
export const SLA_DIAGRAM_NO_SLA_EVENT_TYPES: readonly string[] = [
  "bpmn:StartEvent",
  "bpmn:EndEvent",
];

export const slaDiagramIsNoSlaEventType = (type: string | null | undefined): boolean =>
  SLA_DIAGRAM_NO_SLA_EVENT_TYPES.includes(type ?? "");

/**
 * Nodi che il diagramma deve mostrare nel dettaglio, senza SLA inclusi.
 * `excludedDesignerIds` sono gli id BPMN degli eventi di inizio/fine (ricavati
 * dall'XML del modello): esclusi perche' gli eventi non portano SLA.
 */
export const slaDiagramDetailNodes = (
  nodes: SlaDiagramNode[],
  excludedDesignerIds: ReadonlySet<string>,
): SlaDiagramNode[] =>
  // Il dettaglio include i nodi del payload, senza SLA compresi: e' la scelta
  // del Server (`has_sla = false` resta una riga, `Service.SLA.pas:846-861`).
  // Nessun filtro per salute, ne' per `active`; esclusi solo inizio/fine.
  nodes.filter((node) => !(node.designerid && excludedDesignerIds.has(node.designerid)));

// ─── Istante del Server e freschezza ─────────────────────────────────────────

/**
 * Istante piu' vecchio fra gli `as_of` dei nodi: e' quello che governa la
 * freschezza. Riusa la soglia dichiarata dal Monitor (`SLA_MONITOR_STALE_AFTER_SECONDS`):
 * il contratto del diagramma non porta un limite di validita', quindi la soglia
 * resta una scelta Client **dichiarata** e non una seconda soglia inventata.
 */
export const slaDiagramOldestAsOf = (nodes: SlaDiagramNode[]): string | null => {
  const values = nodes
    .map((node) => node.as_of)
    .filter((value): value is string => Boolean(value));
  if (values.length === 0) return null;
  return values.reduce((oldest, value) =>
    Date.parse(value) < Date.parse(oldest) ? value : oldest,
  );
};

/**
 * Dati scaduti: `as_of` piu' vecchio della soglia dichiarata rispetto al clock
 * del browser. Con dati scaduti la superficie non mostra stato verde (sez. 5 F3d,
 * sez. 8 riga 135). Il test la fissa con il clock del browser e un `as_of` noto,
 * mai con attese reali. Da F3d e' valutata su un orologio che avanza
 * (`useSlaRuntimeRefresh`), non solo al render.
 */
export const slaDiagramIsStale = (
  nodes: SlaDiagramNode[],
  nowMs: number,
  staleAfterSeconds: number = SLA_MONITOR_STALE_AFTER_SECONDS,
): boolean => isSlaMonitorStale(slaDiagramOldestAsOf(nodes), nowMs, staleAfterSeconds);

/**
 * Soglia/apertura (sez. 5 F3d): un nodo dichiara `next_clock_change_at` e da
 * quell'istante il payload non descrive piu' il clock. La superficie rilegge dal
 * Server invece di dedurre la riapertura del calendario nel browser; la regola e'
 * una sola, condivisa con il Monitor (`slaMonitorNeedsRefetch`).
 */
export const slaDiagramNeedsRefetch = (
  nodes: SlaDiagramNode[],
  nowMs: number,
): boolean => slaMonitorNeedsRefetch(nodes, nowMs);

// ─── Limiti dichiarati (non aggirati, sez. 11.3) ─────────────────────────────

/**
 * `limit`: le istanze contate in `other_revision_instances` sono su revisione di
 * origine diversa ma sullo **stesso** `designerid`, e il nodo esiste ancora nel
 * modello corrente (`Service.SLA.pas:872-874` + join sui nodi correnti `:846-861`).
 * Va comunicato come istanze su una revisione precedente, **non** come stato del
 * nodo: non contribuiscono a `worst_health`.
 */
export const SLA_DIAGRAM_OTHER_REVISION_LIMIT = "same_designer_only" as const;

/**
 * `limit`: le istanze su revisioni precedenti il cui nodo non esiste piu' nel
 * modello corrente non compaiono in nessuna riga del diagramma. L'endpoint non
 * offre un contenitore per esse e il Client non lo inventa (decisione 9 del
 * 2026-09-20: "si lascia com'e', limite documentato"). Il recupero resta
 * possibile dalla lista Monitor (F3a).
 */
export const SLA_DIAGRAM_REMOVED_NODES_LIMIT = "removed_nodes_absent" as const;

// ─── Contratto DOM e rotte ───────────────────────────────────────────────────

/**
 * Rotta dedicata della superficie: vista aperta dalla pagina Modelli
 * (`src/pages/SlaDiagram.tsx`). La rotta storica `processes/sla-diagram`
 * reindirizza qui (`src/App.tsx`).
 */
export const SLA_DIAGRAM_ROUTE = "/dashboard/process/sla-diagram";

/**
 * Rotta del drill-down: **riusa la superficie F3a** (sez. 5 F3b), non una lista
 * nuova. Il contratto di `/instances` (`Controller.SLA.pas:309-331`) accetta solo
 * `level`, `health`, `page`, `pagesize`: **non** esiste un filtro per
 * `target_designerid` e non esiste nemmeno un modo di pre-selezionare il livello
 * dalla rotta. Il drill-down apre quindi la lista come il Monitor la apre da se'
 * (tutti i livelli) e **non** puo' restringere la lista al singolo target nel
 * browser: sarebbe un filtro locale, vietato da sez. 5 F3a. Residuo dichiarato,
 * non simulato (i nodi del diagramma sono comunque attivita': il SQL filtra
 * `i.target_type = 2`, `Service.SLA.pas:869-870`).
 */
export const SLA_DIAGRAM_DRILLDOWN_ROUTE = "/dashboard/processes/sla";

/**
 * Configurazione SLA del nodo: designer del modello con il nodo preselezionato
 * (`?node=`), dove la sezione "SLA" del pannello proprieta' edita il target.
 */
export const slaDiagramConfigureRoute = (
  modelId: number,
  designerid: string | null | undefined,
): string =>
  `/dashboard/process/edit/${modelId}${
    designerid ? `?node=${encodeURIComponent(designerid)}` : ""
  }`;

/** Radici asseribili: `sla-diagram-node-<idnode>` (canvas) e `-list-node-`. */
export const slaDiagramNodeTestId = (idnode: number, part?: string): string =>
  part ? `sla-diagram-node-${idnode}-${part}` : `sla-diagram-node-${idnode}`;

export const slaDiagramListNodeTestId = (idnode: number, part?: string): string =>
  part ? `sla-diagram-list-node-${idnode}-${part}` : `sla-diagram-list-node-${idnode}`;

/** Chiave dell'overlay bpmn-js sul nodo: il `designerid` e' l'id BPMN dell'elemento. */
export const slaDiagramElementId = (node: SlaDiagramNode): string | null =>
  node.designerid && node.designerid.length > 0 ? node.designerid : null;

/**
 * Nome leggibile del nodo. Il payload lo manda sempre (`coalesce(name, '')`) ma
 * puo' essere vuoto: in quel caso si rende il `designerid` (dato del payload),
 * non un nome inventato.
 */
export const slaDiagramNodeName = (node: SlaDiagramNode): string =>
  node.name && node.name.length > 0 ? node.name : node.designerid ?? "";

// ─── Istanza piu' urgente (resa dal Server, non scelta nel browser) ───────────

/**
 * Secondi residui della **sola** istanza piu' urgente del nodo, gia' calcolati
 * dal Server (`fl_sla_instance_clock`, `Service.SLA.pas:899`). `null` quando il
 * nodo non ha istanze aperte o il valore non e' un numero: nessuna sottrazione di
 * calendario nel browser (sez. 1).
 */
export const slaDiagramRemainingSeconds = (
  node: Pick<SlaDiagramNode, "remaining_working_seconds">,
): number | null =>
  typeof node.remaining_working_seconds === "number" &&
  Number.isFinite(node.remaining_working_seconds) &&
  node.remaining_working_seconds >= 0
    ? node.remaining_working_seconds
    : null;
